/**
 * "Give me a live connection, reconnecting if needed" for write paths (#243),
 * after the TurboTwin webclient's `useEnsureAssetConnection`.
 *
 * A save that meets a dropped socket used to fail at once with "Not connected
 * to MineTwin module", while the reconnect hooks were seconds away from
 * healing it. This asks every module to reconnect now (the same wake path as
 * tab-visible / online), then waits up to `timeoutMs` for this one to be live.
 */
import { useCallback, useRef } from 'react';
import { useSpacetimeDB } from 'spacetimedb/react';
import { requestReconnectNow } from './useAutoReconnect';

type Live = { isActive?: boolean };

export function useEnsureConnection<C>(key: string): (timeoutMs?: number) => Promise<C | null> {
  const state = useSpacetimeDB(key);
  const stateRef = useRef(state);
  stateRef.current = state;

  return useCallback(async (timeoutMs = 10_000) => {
    const live = (): C | null => {
      const st = stateRef.current;
      const c = st.getConnection() as unknown as (C & Live) | null;
      return st.isActive && c && c.isActive !== false ? c : null;
    };
    const up = live();
    if (up) return up;
    requestReconnectNow();
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 250));
      const c = live();
      if (c) return c;
    }
    return null;
  }, []);
}
